/**
 * Fiado: what clients pay off what they owe.
 *
 * The debt itself is made by a sale with a `credito` payment (see `ventas.ts`), which books it to
 * cuentas por cobrar and adds it to the client's `saldo`. This is the other half: the money
 * coming in, a bit at a time, the way a neighbour pays off the notebook on payday. What the
 * notebook already said they owed, carried over on the day the shop started here. And what will
 * never come in, given up on.
 */

import {
  ZERO,
  addMoney,
  can,
  centavos,
  fechaEnNicaragua,
  formatMoney,
  orderKey,
  subMoney,
  type AnyDomainEvent,
  type Centavos,
  type CuentaBanco,
} from '@abastece/shared';
import { credit, debit, entry } from '../ledger/journal.js';
import type { AccountKey } from '../ledger/accounts.js';
import type { Abono, ApplyResult, Cliente, Effect, Incobrable, Reader } from '../state.js';
import { cuentaCerradaParaSiempre, cuentaDeTransferencia } from './bancos.js';
import { contadaDespues, diaDeLaConfirmacion, yaLaContaron } from './contadas.js';

/** Where the money lands when it does not go into a drawer. */
const CUENTA_ABONO: Record<Abono['formaPago'], AccountKey> = {
  efectivo_cordobas: 'CAJA_FUERTE',
  transferencia: 'BANCOS',
  billetera: 'BILLETERA_MOVIL',
};

/**
 * A drawer's cash when the abono went into one; a transfer, the bank account it went to
 * (`bancos.ts`); otherwise the account the form of payment names.
 */
const cuentaDe = (a: Pick<Abono, 'formaPago' | 'sesionId' | 'cuenta' | 'estadoPago'>): AccountKey =>
  a.sesionId
    ? 'CAJA_CORDOBAS'
    : a.estadoPago === 'pendiente'
      ? 'TRANSFERENCIAS_PENDIENTES'
      : a.formaPago === 'transferencia'
        ? cuentaDeTransferencia(a.cuenta as CuentaBanco | null | undefined)
        : CUENTA_ABONO[a.formaPago];

/**
 * `baja` off what `cliente` owes, as of `fecha`: the patch, and the entry that brings back what of
 * it had been given up on.
 *
 * More off than they owe leaves money in their favour — the truth when they paid twice, not when
 * the shop had given the debt up: they never paid that, and what comes off is the fiado given up
 * on (`Cliente.perdido`). A sale voided after its fiado was written off left the client C$ 220 in
 * their favour and the C$ 220 still a loss (2026-10-13). So that part comes back out of «Fiado
 * incobrable», on the day it happens, as an undone write-off does (`deshacerPerdido`).
 */
export function bajarDeuda(
  cliente: Cliente,
  baja: Centavos,
  eventId: string,
  fecha: string,
  porQue: string,
): { patch: Partial<Cliente>; asiento: Effect | null; recuperado: Centavos } {
  const saldo = cliente.saldo ?? ZERO;
  const perdido = cliente.perdido ?? ZERO;
  const recuperado = centavos(Math.max(0, Math.min(perdido, baja - Math.max(saldo, 0))));
  if (recuperado === 0) return { patch: { saldo: subMoney(saldo, baja) }, asiento: null, recuperado };
  return {
    patch: {
      saldo: addMoney(subMoney(saldo, baja), recuperado),
      perdido: subMoney(perdido, recuperado),
      recuperado: addMoney(cliente.recuperado ?? ZERO, recuperado),
    },
    asiento: {
      kind: 'asiento',
      entry: entry(eventId, fecha, `Fiado de ${cliente.nombre} ya no perdido: ${porQue}`, [
        debit('CUENTAS_POR_COBRAR', recuperado),
        credit('FIADO_INCOBRABLE', recuperado),
      ]),
    },
    recuperado,
  };
}

/**
 * An abono undone — voided, or its payment never arrived: they owe `monto` again, except what of it
 * had paid what was given up on (`Abono.recuperado`), which is given up on again. As far as that
 * still stands (`Cliente.recuperado`): a write-off undone since made it owed again already.
 */
function volverADeber(cliente: Cliente, abono: Abono, eventId: string, fecha: string, porQue: string, rev: number): Effect[] {
  const vuelve = centavos(Math.min(abono.recuperado ?? ZERO, cliente.recuperado ?? ZERO));
  const saldo = subMoney(addMoney(cliente.saldo ?? ZERO, abono.monto), vuelve);
  if (vuelve === 0) return [{ kind: 'cliente.patch', id: cliente.id, rev, patch: { saldo } }];
  return [
    {
      kind: 'cliente.patch',
      id: cliente.id,
      rev,
      patch: { saldo, perdido: addMoney(cliente.perdido ?? ZERO, vuelve), recuperado: subMoney(cliente.recuperado ?? ZERO, vuelve) },
    },
    {
      kind: 'asiento',
      entry: entry(eventId, fecha, `Fiado de ${cliente.nombre} perdido otra vez: ${porQue}`, [
        debit('FIADO_INCOBRABLE', vuelve),
        credit('CUENTAS_POR_COBRAR', vuelve),
      ]),
    },
  ];
}

/**
 * What still stands of each of a client's write-offs. What came back of them (`Cliente.recuperado`)
 * comes off the ones given up on last; one undone stands at nothing. The client's account shows
 * these, and undoing one makes its own owed again — the same figure on the screen and in the books.
 * Newest by day, then by id, which every device has the same.
 */
export function perdidoQueSigue(incobrables: readonly Incobrable[], recuperado: Centavos | null | undefined): Map<string, Centavos> {
  const activos = incobrables
    .filter((x) => !x.anulado)
    .sort((a, b) => b.fecha.localeCompare(a.fecha) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
  let volvio: number = recuperado ?? 0;
  const sigue = new Map<string, Centavos>();
  for (const x of activos) {
    const toma = Math.min(volvio, x.monto);
    volvio -= toma;
    sigue.set(x.id, centavos(x.monto - toma));
  }
  return sigue;
}

export async function reduceFiado(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  const rev = orderKey(event);
  switch (event.type) {
    case 'abono.registrado':
      return registrar(event, read, rev);
    case 'abono.anulado':
      return anular(event, read, rev);
    case 'cliente.saldo_inicial':
      return saldoInicial(event, read, rev);
    case 'incobrable.registrado':
      return darPorPerdido(event, read, rev);
    case 'incobrable.anulado':
      return deshacerPerdido(event, read, rev);
    default:
      return null;
  }
}

/**
 * What they owed before the shop kept fiado here.
 *
 * Only the difference from what was set before moves, so typing it again corrects it instead of
 * adding a second debt. Against Capital, as opening stock is: a debt the owner was owed already,
 * which no sale here made. Booked to income it would be a sale that never happened; left out, the
 * books would say the client owes less than the shop is going to collect.
 */
async function saldoInicial(
  event: Extract<AnyDomainEvent, { type: 'cliente.saldo_inicial' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });

  const cliente = await read.cliente(p.clienteId);
  if (!cliente) return rechazo('El cliente no existe');
  const monto = centavos(p.monto);
  const diferencia = subMoney(monto, cliente.saldoInicial ?? ZERO);
  // The same figure again, typed on two phones, say: nothing to do, and nothing for the owner to
  // review among the refused, as with opening stock counted twice the same.
  if (diferencia === 0) return { effects: [], rejections: [] };

  // The day it is written down, as with opening stock: the log's day, never the clock's.
  const fecha = fechaEnNicaragua(event.clientTs);
  const cuanto = centavos(Math.abs(diferencia));
  // Lowered after it was given up on: what was never owed was never lost either (`bajarDeuda`).
  const baja =
    diferencia < 0
      ? bajarDeuda(cliente, cuanto, event.id, fecha, 'el saldo anterior era menos')
      : { patch: { saldo: addMoney(cliente.saldo ?? ZERO, diferencia) }, asiento: null };
  return {
    effects: [
      {
        kind: 'cliente.patch',
        id: cliente.id,
        rev,
        patch: {
          ...baja.patch,
          saldoInicial: monto,
          saldoInicialFecha: monto > 0 ? (cliente.saldoInicialFecha ?? fecha) : null,
        },
      },
      {
        kind: 'asiento',
        entry: entry(
          event.id,
          fecha,
          `Saldo anterior de ${cliente.nombre}`,
          diferencia > 0
            ? [debit('CUENTAS_POR_COBRAR', cuanto), credit('CAPITAL', cuanto)]
            : [debit('CAPITAL', cuanto), credit('CUENTAS_POR_COBRAR', cuanto)],
        ),
      },
      ...(baja.asiento ? [baja.asiento] : []),
    ],
    rejections: [],
  };
}

async function registrar(
  event: Extract<AnyDomainEvent, { type: 'abono.registrado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });

  if (await read.abono(p.abonoId)) return rechazo('Ese abono ya fue registrado');
  const cliente = await read.cliente(p.clienteId);
  if (!cliente) return rechazo('El cliente no existe');

  /**
   * Cash goes into a till, where the corte expects it. Taken with no till open it could only go
   * to general cash on somebody's word — and general cash is counted by nobody at the counter.
   */
  if (p.formaPago === 'efectivo_cordobas' && !p.sesionId) {
    return rechazo('Un abono en efectivo entra a una caja abierta');
  }
  const cerrada = cuentaCerradaParaSiempre(await read.config(), p.cuenta);
  if (cerrada) return rechazo(cerrada);
  const sesion = p.sesionId ? await read.sesionCaja(p.sesionId) : undefined;
  if (p.sesionId) {
    if (p.formaPago !== 'efectivo_cordobas') return rechazo('Solo el efectivo entra a la gaveta');
    if (!sesion) return rechazo('La caja de este abono no existe');
    if (sesion.estado !== 'abierta') return rechazo('La caja de este abono ya fue cerrada');
  }

  /**
   * A screenshot is not a payment (CONTROLES item 3), on an account no more than at the till: a
   * transfer or a wallet payment taken by somebody who does not confirm them waits in the queue,
   * its money in «Transferencias pendientes», until somebody sees it arrive — and if it never does,
   * the client owes it again (`rechazarPagoAbono`). It used to go straight to the bank as money in
   * hand: a made-up screenshot cleared a debt and nothing ever asked about it. The owner, who
   * confirms them, takes one as seen.
   */
  const enCola = p.formaPago !== 'efectivo_cordobas' && !(await confirmaTransferencias(read, event.actorId));

  /**
   * Paying more than is owed is not refused. The money was handed over, maybe at a till that
   * could not see a payment another phone took an hour ago; refusing would only lose the record
   * of it. The client ends up with money in their favour, which is the truth.
   */
  const monto = centavos(p.monto);
  // More than they owe, while some was given up on, pays that first (`bajarDeuda`).
  const baja = bajarDeuda(cliente, monto, event.id, p.fecha, 'pagó lo que se había dado por perdido');
  const abono: Abono = {
    id: p.abonoId,
    clienteId: p.clienteId,
    fecha: p.fecha,
    monto,
    formaPago: p.formaPago,
    sesionId: p.sesionId ?? null,
    ...(p.cuenta ? { cuenta: p.cuenta } : {}),
    referencia: p.referencia ?? null,
    nota: p.nota ?? null,
    anulado: false,
    motivoAnulacion: null,
    ...(enCola ? { estadoPago: 'pendiente' as const } : {}),
    ...(baja.recuperado > 0 ? { recuperado: baja.recuperado } : {}),
    rev,
  };

  const effects: Effect[] = [
    { kind: 'abono.upsert', id: p.abonoId, row: abono },
    { kind: 'cliente.patch', id: cliente.id, rev, patch: baja.patch },
    {
      kind: 'asiento',
      entry: entry(event.id, p.fecha, `Abono de ${cliente.nombre}`, [
        debit(cuentaDe(abono), monto, p.referencia),
        credit('CUENTAS_POR_COBRAR', monto),
      ]),
    },
    ...(baja.asiento ? [baja.asiento] : []),
  ];
  if (sesion) {
    effects.push({
      kind: 'sesion.patch',
      id: sesion.id,
      rev,
      patch: { abonos: addMoney(sesion.abonos ?? ZERO, monto) },
    });
  }
  return { effects, rejections: [] };
}

async function anular(
  event: Extract<AnyDomainEvent, { type: 'abono.anulado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const { abonoId, motivo, sesionId: cajaId } = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });

  const abono = await read.abono(abonoId);
  if (!abono) return rechazo('Ese abono no existe');
  if (abono.anulado) return rechazo('Ese abono ya está anulado');
  /*
   * Into the bank or the wallet, and a statement squared since that reads to its day: the statement
   * already found what the money is, and the difference took it in. Voided now, it went out of a day
   * the statement read right, and the next statement booked it back (2026-10-20). As a deposit's
   * void, it is past voiding: its place in the log, the abono's last write (it has no `lugar`).
   */
  if (!abono.sesionId && abono.estadoPago !== 'pendiente') {
    const config = await read.config();
    if (contadaDespues(config, cuentaDe(abono), abono.rev, abono.fecha)) return rechazo(yaLaContaron(cuentaDe(abono), 'ese abono', config));
  }

  /**
   * Where the cash comes back from, as with a voided sale. While the abono's till is open, from
   * that drawer: it simply stops expecting the money. Once that till has closed, its money went to
   * general cash with the corte, so the cash comes back out of a till open now, which then expects
   * it gone. Without one there is nowhere for it to come from.
   */
  const sesion = abono.sesionId ? await read.sesionCaja(abono.sesionId) : undefined;
  const turnoAbierto = sesion?.estado === 'abierta';
  const deUnaCaja = abono.sesionId !== null && !turnoAbierto;
  const caja = deUnaCaja && cajaId ? await read.sesionCaja(cajaId) : undefined;
  if (deUnaCaja) {
    if (!caja) return rechazo('Ese abono es de un turno cerrado: su efectivo se devuelve de una caja abierta');
    if (caja.estado !== 'abierta') return rechazo('La caja de este reembolso ya fue cerrada');
  }

  // Its till closed, the cash comes back out of today's drawer today (`venta.anulada`'s rule).
  const fecha = caja ? fechaEnNicaragua(event.clientTs) : abono.fecha;
  const effects: Effect[] = [
    { kind: 'abono.patch', id: abonoId, rev, patch: { anulado: true, motivoAnulacion: motivo } },
    {
      kind: 'asiento',
      entry: entry(event.id, fecha, `Abono anulado: ${motivo}`, [
        debit('CUENTAS_POR_COBRAR', abono.monto),
        credit(cuentaDe(abono), abono.monto),
      ]),
    },
  ];

  const cliente = await read.cliente(abono.clienteId);
  if (cliente) effects.push(...volverADeber(cliente, abono, event.id, fecha, `abono anulado: ${motivo}`, rev));

  if (sesion && turnoAbierto) {
    effects.push({
      kind: 'sesion.patch',
      id: sesion.id,
      rev,
      patch: { abonos: subMoney(sesion.abonos ?? ZERO, abono.monto) },
    });
  }
  if (caja) {
    effects.push({
      kind: 'sesion.patch',
      id: caja.id,
      rev,
      patch: { devoluciones: addMoney(caja.devoluciones ?? ZERO, abono.monto) },
    });
  }
  return { effects, rejections: [] };
}

/**
 * Fiado the shop gives up on: off what the client owes, and against «Fiado incobrable» on the day
 * it is given up, which is when it costs the shop. Without it, a debt nobody will pay stays in
 * «Cuentas por cobrar» for ever, or leaves it as an abono that never happened.
 *
 * Never more than they owe. Two phones can each see the whole balance, and the second, arriving
 * after an abono or the first write-off, would give up money nobody owes, leaving the client in
 * credit for a debt they never paid. So it is refused with what is owed now, and the owner gives
 * up what is left.
 */
async function darPorPerdido(
  event: Extract<AnyDomainEvent, { type: 'incobrable.registrado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });

  if (await read.incobrable(p.incobrableId)) return rechazo('Eso ya se dio por perdido');
  const cliente = await read.cliente(p.clienteId);
  if (!cliente) return rechazo('El cliente no existe');
  const debe = cliente.saldo ?? ZERO;
  if (debe <= 0) return rechazo(`${cliente.nombre} no debe nada`);
  const monto = centavos(p.monto);
  if (monto > debe) return rechazo(`${cliente.nombre} debe ${formatMoney(debe)}: no se puede dar por perdido más de eso`);

  const incobrable: Incobrable = {
    id: p.incobrableId,
    clienteId: cliente.id,
    fecha: p.fecha,
    monto,
    motivo: p.motivo,
    anulado: false,
    motivoAnulacion: null,
    rev,
  };
  return {
    effects: [
      { kind: 'incobrable.upsert', id: incobrable.id, row: incobrable },
      {
        kind: 'cliente.patch',
        id: cliente.id,
        rev,
        patch: { saldo: subMoney(debe, monto), perdido: addMoney(cliente.perdido ?? ZERO, monto) },
      },
      {
        kind: 'asiento',
        entry: entry(event.id, p.fecha, `Fiado de ${cliente.nombre} dado por perdido: ${p.motivo}`, [
          debit('FIADO_INCOBRABLE', monto),
          credit('CUENTAS_POR_COBRAR', monto),
        ]),
      },
    ],
    rejections: [],
  };
}

/**
 * They came back and paid, or it was a mistake: they owe it again. Booked on the day it is undone,
 * not the day it was given up. What usually undoes one is money turning up months later, and that
 * is this month's news: taking it back out of an old month would change a profit the owner has
 * already read.
 */
async function deshacerPerdido(
  event: Extract<AnyDomainEvent, { type: 'incobrable.anulado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const { incobrableId, motivo } = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });

  const incobrable = await read.incobrable(incobrableId);
  if (!incobrable) return rechazo('Eso no se dio por perdido');
  if (incobrable.anulado) return rechazo('Eso ya se deshizo');
  const cliente = await read.cliente(incobrable.clienteId);
  if (!cliente) return rechazo('El cliente no existe');

  /**
   * Owed again: what of it still stands (`perdidoQueSigue`). What already came back — paid, or
   * taken off with a void or a return (`bajarDeuda`) — is not owed a second time.
   */
  const vuelve = perdidoQueSigue(await read.incobrablesDe(cliente.id), cliente.recuperado).get(incobrable.id) ?? ZERO;
  if (vuelve === 0) return rechazo(`Lo que se dio por perdido de ${cliente.nombre} ya volvió: no queda nada que deshacer`);
  return {
    effects: [
      { kind: 'incobrable.patch', id: incobrableId, rev, patch: { anulado: true, motivoAnulacion: motivo } },
      {
        kind: 'cliente.patch',
        id: cliente.id,
        rev,
        patch: {
          saldo: addMoney(cliente.saldo ?? ZERO, vuelve),
          perdido: subMoney(cliente.perdido ?? ZERO, vuelve),
          // What of this one had come back stops standing with it.
          recuperado: centavos(Math.max(0, (cliente.recuperado ?? ZERO) - (incobrable.monto - vuelve))),
        },
      },
      {
        kind: 'asiento',
        entry: entry(event.id, fechaEnNicaragua(event.clientTs), `Fiado de ${cliente.nombre} ya no perdido: ${motivo}`, [
          debit('CUENTAS_POR_COBRAR', vuelve),
          credit('FIADO_INCOBRABLE', vuelve),
        ]),
      },
    ],
    rejections: [],
  };
}

/** Whether this person confirms transfers (`confirmar_transferencia`): the owner's, as the queue is. */
export async function confirmaTransferencias(read: Reader, actorId: string): Promise<boolean> {
  const quien = await read.usuario(actorId);
  // A shop with nobody in it yet is being set up, as `permisos.ts` lets it be.
  if (!quien) return !(await read.hayUsuarios());
  return Boolean(quien.activo && can(quien.rol, 'confirmar_transferencia'));
}

/** An abono's transfer or wallet payment seen to arrive: out of the queue, into the account it was seen in. */
export async function confirmarPagoAbono(
  eventId: string,
  abonoId: string,
  referencia: string | undefined,
  read: Reader,
  rev: number,
  /** The day it was seen at the bank or the wallet (`diaDeLaConfirmacion`). */
  diaVisto: string,
  /** The córdoba account a transfer was seen in (`bancos.ts`); absent, the one the abono named, or «Bancos». */
  cuenta?: CuentaBanco,
): Promise<ApplyResult> {
  const a = await read.abono(abonoId);
  if (!a) return { effects: [], rejections: [{ eventId, reason: 'Ese abono no existe' }] };
  if (a.estadoPago !== 'pendiente' || a.anulado) return { effects: [], rejections: [{ eventId, reason: 'Ese pago ya fue resuelto' }] };
  const visto = { ...a, estadoPago: 'confirmado' as const, ...(a.formaPago === 'transferencia' && cuenta ? { cuenta } : {}) };
  const ref = referencia ?? a.referencia ?? '';
  const fecha = diaDeLaConfirmacion(await read.config(), cuentaDe(visto), a.fecha, diaVisto);
  return {
    effects: [
      { kind: 'abono.patch', id: a.id, rev, patch: { estadoPago: 'confirmado', referencia: ref || null, ...(visto.cuenta ? { cuenta: visto.cuenta } : {}) } },
      {
        kind: 'asiento',
        entry: entry(eventId, fecha, `Abono de ${(await read.cliente(a.clienteId))?.nombre ?? ''}: pago confirmado`, [
          debit(cuentaDe(visto), a.monto, ref),
          credit('TRANSFERENCIAS_PENDIENTES', a.monto),
        ]),
      },
    ],
    rejections: [],
  };
}

/** An abono's payment that never arrived: the client owes it again, as if it had never been made. */
export async function rechazarPagoAbono(
  eventId: string,
  abonoId: string,
  motivo: string,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const a = await read.abono(abonoId);
  if (!a) return { effects: [], rejections: [{ eventId, reason: 'Ese abono no existe' }] };
  if (a.estadoPago !== 'pendiente' || a.anulado) return { effects: [], rejections: [{ eventId, reason: 'Ese pago ya fue resuelto' }] };
  const cliente = await read.cliente(a.clienteId);
  return {
    effects: [
      {
        kind: 'abono.patch',
        id: a.id,
        rev,
        patch: { estadoPago: 'rechazado', anulado: true, motivoAnulacion: motivo },
      },
      ...(cliente ? volverADeber(cliente, a, eventId, a.fecha, `el pago no llegó: ${motivo}`, rev) : []),
      {
        kind: 'asiento',
        entry: entry(eventId, a.fecha, `Abono de ${cliente?.nombre ?? ''}: el pago no llegó: ${motivo}`, [
          debit('CUENTAS_POR_COBRAR', a.monto),
          credit('TRANSFERENCIAS_PENDIENTES', a.monto),
        ]),
      },
    ],
    rejections: [],
  };
}
